/**
 * Bank-statement header card: account icon + name + period text, plus
 * the month/year selectors, prev/next-month arrows, tier-horizon upsell
 * link, and the right-hand ExportMenu.
 *
 * Pure view — every state value + handler arrives as a prop. Renders the
 * "Opening balance" row at the bottom of the card so the entries table
 * (next card section) starts cleanly.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ExportMenu from '../../components/ExportMenu';
import { fmtMoney } from '../../lib/format';
import { TAG_LABELS, computeTag } from '../../lib/reconcileTags';
import { formatDateUK } from './constants';

const StatementHeader = ({
  account, type, Icon, isLiability, period, monthLabel,
  year, month, setYear, setMonth, stepMonth,
  yearOptions, monthOptions, atCap, tierHorizonMonths, tierLabel,
  opening_balance, entries, todayIso, showAsCredit,
}) => (
  <>
    <div className="px-6 sm:px-10 py-6 border-b border-[#E2E8F0] bg-[#F8FAFC]">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isLiability ? 'bg-[#FEE2E2] text-[#991B1B]' : 'bg-[#DBEAFE] text-[#1E40AF]'}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Forge Ledger Statement</div>
            <h1 className="text-2xl tracking-tight font-bold text-[#020617]" data-testid="statement-account-name">{account.name}</h1>
            <div className="text-sm text-[#64748B] mt-0.5">{type?.name || account.account_type} · {account.currency}</div>
          </div>
        </div>
        <div className="text-sm text-[#020617] sm:text-right">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Period</div>
          <div className="font-medium" data-testid="statement-period">{formatDateUK(period.start_date)} – {formatDateUK(period.end_date)}</div>
        </div>
      </div>

      {/* Month / Year selectors + arrows (hidden in PDF — period text below shows month) */}
      <div className="flex flex-wrap items-center gap-3 mt-6">
        <span className="print-only text-base font-bold text-[#020617]" data-testid="statement-period-label-print">
          {monthLabel} {year}
        </span>
        <button onClick={() => stepMonth(-1)} className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-[#E2E8F0] bg-white hover:bg-[#F1F5F9] transition-colors print-hide" aria-label="Previous month" data-testid="prev-month-button">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="print-hide">
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
            <SelectTrigger className="w-40 bg-white" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="print-hide">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-28 bg-white" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={() => stepMonth(1)}
          disabled={atCap}
          className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-[#E2E8F0] bg-white hover:bg-[#F1F5F9] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors print-hide"
          aria-label={atCap ? `Forecast horizon reached (${tierLabel} = ${tierHorizonMonths} months)` : 'Next month'}
          title={atCap ? `Forge Ledger ${tierLabel} can forecast up to ${tierHorizonMonths} months ahead. Upgrade to see further.` : 'Next month'}
          data-testid="next-month-button"
        >
          {atCap ? <Lock className="w-3.5 h-3.5" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {atCap && tierHorizonMonths < 36 && (
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 text-xs text-[#475569] hover:text-[#020617] transition-colors print-hide"
            data-testid={`statement-horizon-upsell-${tierHorizonMonths < 18 ? 'pro' : 'pro_plus'}`}
          >
            <span>See {tierHorizonMonths < 18 ? '18' : '36'} months ahead with</span>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
              style={{
                backgroundImage:
                  tierHorizonMonths < 18
                    ? 'linear-gradient(135deg, #64748B 0%, #0F172A 100%)'
                    : 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
              }}
            >
              {tierHorizonMonths < 18 ? 'Steel →' : 'Damascus →'}
            </span>
          </Link>
        )}
        <div className="ml-auto">
          <ExportMenu
            testIdPrefix="statement-export"
            getData={() => ({
              fileName: `forge-ledger-statement-${(account?.name || 'account').toLowerCase().replace(/\s+/g, '-')}-${period.year}-${String(period.month).padStart(2, '0')}`,
              sheetName: `${monthLabel} ${period.year}`.slice(0, 31),
              columns: [
                { header: 'Date', key: 'date', width: 12 },
                { header: 'Description', key: 'description', width: 32 },
                { header: 'Tag', key: 'tag', width: 12 },
                { header: 'Credit (GBP)', key: 'credit', width: 14 },
                { header: 'Debit (GBP)', key: 'debit', width: 14 },
                { header: 'Balance (GBP)', key: 'balance', width: 14 },
                { header: 'Category', key: 'category', width: 16 },
                { header: 'Estimated', key: 'estimated', width: 10 },
              ],
              rows: entries.map((e) => {
                const credit = showAsCredit(e.transaction_type);
                return {
                  date: e.date,
                  description: e.description,
                  tag: TAG_LABELS[computeTag({ date: e.date, reconciled: e.source !== 'projected' && !!e.reconciled }, todayIso)],
                  credit: credit ? e.amount : '',
                  debit: !credit ? e.amount : '',
                  balance: e.running_balance,
                  category: e.category || '',
                  estimated: e.is_estimated ? 'Yes' : '',
                };
              }),
            })}
          />
        </div>
      </div>
    </div>

    {/* Opening balance */}
    <div className="px-6 sm:px-10 py-4 flex items-center justify-between border-b border-[#E2E8F0]">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Opening balance</div>
      <div className={`text-lg font-semibold ${opening_balance < 0 ? 'text-[#EF4444]' : 'text-[#020617]'}`} data-testid="opening-balance">£{fmtMoney(opening_balance)}</div>
    </div>
  </>
);

export default StatementHeader;
